var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * React-query hook for the form-vs-corpus analysis (CZNA-478, made asynchronous
 * in CZNA-508).
 *
 * The endpoint answers 202 while a background pass over the corpus runs and 200
 * once a result is held, so `fetchFormAnalysis` resolves to `null` in the first
 * case and this hook polls until it flips. That is what keeps the request short:
 * the aggregation costs ~29 s for PERSONAL, and Kong's `read_timeout` on the
 * test cluster is 60 s, so holding one request open for the whole computation
 * was never going to survive corpus growth.
 *
 * `enabled` is caller-controlled so opening the settings page does not fire an
 * expensive aggregation nobody asked for; the query runs when the user picks
 * the action.
 *
 * Closure-captures `ky` (per MEMORY: `useOkapiKy` returns an unstable instance
 * and must NOT go into dependency arrays).
 */
import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useOkapiKy } from '@folio/stripes/core';
import { fetchFormAnalysis, refreshFormAnalysis, } from '../api/formAnalysisService';
/**
 * How often to re-ask while the backend is computing.
 *
 * Two seconds against a ~29 s job is ~15 polls — cheap, because a poll that
 * finds the work unfinished costs a map lookup on the backend, not a query.
 */
export var FORM_ANALYSIS_POLL_MS = 2000;
/**
 * How long to keep asking before telling the reader the analysis has not
 * answered (CZNA-782).
 *
 * Without a ceiling a result that never comes is a skeleton that never goes
 * away — which is what the NK demo on 25. 8. 2026 saw: a 665-record type took
 * 229 s on the test cluster and nobody could tell whether it was working. The
 * backend bounds each aggregation statement now, but nothing it does can cover
 * a job still queued behind a corpus revalidation on the shared executor, so the
 * limit has to live here as well.
 *
 * Stopping the poll does not stop the job. The backend holds the result once it
 * lands, so the next open (or "check again") picks it up with a single 200.
 * Five minutes is past every measured type except the two largest on the test
 * cluster, for which that is exactly the message the reader should get.
 */
export var FORM_ANALYSIS_MAX_WAIT_MS = 5 * 60000;
export var useFormAnalysis = function (type, enabled) {
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    var queryKey = ['form-analysis', type];
    // Flipped by a timer rather than derived from elapsed time at render: the
    // poll interval and the view must agree on "gave up", and two separate
    // Date.now() reads can land either side of the limit and leave a skeleton
    // with no poll behind it.
    var _a = useState(false), timedOut = _a[0], setTimedOut = _a[1];
    var query = useQuery(queryKey, function () { return fetchFormAnalysis(ky, type); }, {
        // Live analytics: never serve a cached aggregate as if it were current.
        // The backend holds the result now, so a refetch on remount is a cheap
        // 200 rather than a recomputation — and it is the backend's `computedAt`,
        // not react-query's cache age, that tells the reader how old it is.
        staleTime: 0,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        // A failed aggregation must not be retried silently — the user would wait
        // several times over with no explanation.
        retry: false,
        enabled: enabled && !!type,
        // null = HTTP 202, the pass is still running. Anything else is terminal.
        //
        // The error state must be checked explicitly (CZNA-519). react-query calls
        // this with `currentResult.data`, which is `undefined` on a failed query —
        // indistinguishable here from the 202 case, so keying on `data` alone kept
        // polling forever against an error that will never resolve. There is no
        // safety net above: `updateRefetchInterval` bails only on isServer /
        // enabled===false / an invalid timeout, never on status, and `retry: false`
        // governs retries of one fetch, not the interval.
        //
        // `timedOut` is the third terminal (CZNA-782): the reader has been told
        // the analysis did not answer, so asking on in the background would only
        // hide a result behind a message that says there is none.
        refetchInterval: function (data, query) { return (data || query.state.status === 'error' || timedOut ? false : FORM_ANALYSIS_POLL_MS); },
    });
    // Only a query actually waiting on the backend can run out of time: a failed
    // poll keeps its last `null` (react-query holds data across an error), and a
    // disabled query is not asking at all.
    var computing = enabled && !!type && !query.isError
        && (query.isLoading || query.data === null);
    useEffect(function () {
        if (!computing || timedOut)
            return undefined;
        var timer = setTimeout(function () { return setTimedOut(true); }, FORM_ANALYSIS_MAX_WAIT_MS);
        return function () { return clearTimeout(timer); };
    }, [computing, timedOut]);
    // A different type is a different wait.
    useEffect(function () {
        setTimedOut(false);
    }, [type]);
    /**
     * Starts waiting again after the limit was hit, without discarding anything:
     * the job may well have finished meanwhile, in which case this one request
     * returns the result.
     */
    var checkAgain = useCallback(function () {
        setTimedOut(false);
        query.refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query.refetch]);
    /**
     * Forces a fresh pass. Clears the cached result first so the view drops
     * straight back to its computing state instead of showing the stale numbers
     * with a spinner beside them, then lets the poll pick the new one up.
     */
    var refresh = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!type)
                        return [2 /*return*/];
                    setTimedOut(false);
                    return [4 /*yield*/, refreshFormAnalysis(ky, type)];
                case 1:
                    _a.sent();
                    queryClient.setQueryData(queryKey, null);
                    return [4 /*yield*/, queryClient.invalidateQueries(queryKey)];
                case 2:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [type, queryClient]);
    return __assign(__assign({}, query), { refresh: refresh, timedOut: timedOut, checkAgain: checkAgain });
};
